export interface BusinessCalendarDefinition {
  code: string;
  versionCode: string;
  label: string;
  posture: string;
  timeZoneId: string;
  workingDays: string[];
  holidays: string[];
  effectiveFrom: string;
  /** Exclusive date-only upper bound. */
  effectiveTo?: string | null;
}
export interface BusinessCalendar extends BusinessCalendarDefinition {
  contentHash: string;
  managed: boolean;
}
export interface BusinessCalendarDraft {
  id: number;
  revision: number;
  eTag: string;
  statusCode: 'draft' | 'published';
  definition: BusinessCalendarDefinition;
  contentHash: string;
  publishedUtc?: string | null;
}
export interface CreateBusinessCalendarDraft {
  displayName: string;
  calendarCode?: string;
}
export interface UpdateBusinessCalendarDraft {
  expectedRevision: number;
  definition: BusinessCalendarDefinition;
}
export interface BusinessCalendarCatalog {
  contractVersion: 1;
  calendars: BusinessCalendar[];
  timeZones?: { id: string; label: string }[];
}
export type CalendarOperation =
  | 'add-calendar-days'
  | 'add-working-days'
  | 'local-to-instant'
  | 'instant-to-local';
export interface CalendarCalculationRequest {
  operation: CalendarOperation;
  calendarCode: string;
  calendarVersionCode: string;
  expectedCalendarContentHash: string;
  date?: string;
  time?: string;
  instant?: string;
  amount?: number;
  explicitOffsetMinutes?: number;
}
export interface CalendarCalculationEvidence {
  operation: CalendarOperation;
  ruleCode: string;
  ruleVersionCode: string;
  calendarCode: string;
  calendarVersionCode: string;
  calendarContentHash: string;
  timeZoneId: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  inputDate: string | null;
  inputTime: string | null;
  inputInstant: string | null;
  amount: number | null;
  explicitOffsetMinutes: number | null;
  resultDate: string | null;
  resultTime: string | null;
  resultInstant: string | null;
  resolvedOffsetMinutes: number | null;
  calculatedAtUtc: string;
  basisTimeZoneId?: string | null;
}
/** A calculation receipt, never a hearing deadline, scheduled event or runtime authorization. */
export interface CalendarCalculationResult {
  contractVersion: 1;
  sideEffectFree: true;
  valueKind: 'date-only' | 'instant' | 'local-date-time';
  date?: string | null;
  time?: string | null;
  instant?: string | null;
  offsetMinutes?: number | null;
  evidence: CalendarCalculationEvidence;
}

export const calendarIdentity = (
  calendar: Pick<BusinessCalendar, 'code' | 'versionCode'>,
) => JSON.stringify([calendar.code, calendar.versionCode]);

/** Keep local wall-clock components as strings; the server owns zone/DST resolution. */
export function calendarTimeInput(value: string): string | null {
  const match = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  return match &&
    Number(match[1]) <= 23 &&
    Number(match[2]) <= 59 &&
    Number(match[3] ?? 0) <= 59
    ? `${match[1]}:${match[2]}:${match[3] ?? '00'}`
    : null;
}
